import { createContext, useContext } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Card } from '@logspot-platform/ui/card';
import { Skeleton } from '@logspot-platform/ui/skeleton';

export type WidgetAction = {
  label: string;
  action: () => void;
};

/**
 * Host-provided renderer for the widget's header menu (e.g. the admin app's
 * dropdown with Delete/Hide/Screenshot). When no provider is mounted, the
 * widget renders without a menu (e.g. in the public embed).
 */
export type WidgetMenuRenderer = (args: {
  actions: WidgetAction[];
  Header?: () => ReactElement;
  children?: ReactNode;
}) => ReactNode;

const WidgetMenuContext = createContext<WidgetMenuRenderer | null>(null);

export const WidgetMenuProvider = ({
  renderMenu,
  children,
}: {
  renderMenu: WidgetMenuRenderer;
  children: ReactNode;
}) => (
  <WidgetMenuContext.Provider value={renderMenu}>
    {children}
  </WidgetMenuContext.Provider>
);

type WidgetShellProps = {
  backgroundColor?: string;
  Header?: () => ReactElement;
  SubHeader?: () => ReactElement;
  children?: ReactNode;
  onDelete?: () => void | Promise<unknown> | undefined;
  onHide?: () => void;
  hideMenu?: boolean;
  isScreenshot?: boolean;
  loading?: boolean;
};

export const Widget = ({
  backgroundColor,
  Header,
  SubHeader,
  children,
  onDelete,
  onHide,
  hideMenu,
  isScreenshot,
  loading,
}: WidgetShellProps) => {
  const renderMenu = useContext(WidgetMenuContext);

  const actions: WidgetAction[] = [];

  if (onDelete) {
    actions.push({ label: 'Delete', action: onDelete });
  }

  if (onHide) {
    actions.push({ label: 'Hide', action: onHide });
  }

  return (
    <Card
      className="light flex min-h-80 w-full flex-col gap-2"
      style={{ background: backgroundColor }}
    >
      {!hideMenu && (
        <div className="flex flex-col gap-2">
          <div className={`flex items-center ${isScreenshot ? '' : 'px-2'}`}>
            <div className="flex-1 px-2">
              <div className="flex-1">{Header && <Header />}</div>
            </div>
            {renderMenu && renderMenu({ actions, Header, children })}
          </div>
          {SubHeader && <SubHeader />}
        </div>
      )}
      {loading ? (
        <div className="m-5 flex h-full flex-col gap-3">
          <div className="flex h-1/6 grow justify-between">
            <div className="flex w-full flex-col gap-2">
              <Skeleton className="h-6 w-3/6" />
              <Skeleton className="h-5 w-2/6" />
            </div>
            <Skeleton className="size-11 w-2/6 rounded" />
          </div>
          <div className="flex h-5/6 flex-col gap-2">
            <Skeleton className="h-1/3 rounded" />
            <Skeleton className="h-2/3 rounded" />
          </div>
        </div>
      ) : (
        <div className="flex h-full min-h-37.5 min-w-0 flex-1 flex-col justify-end">
          {children}
        </div>
      )}
    </Card>
  );
};
